import { Language } from './translations';
import { IBreadcrumbItem } from '@/interfaces';

/**
 * Generate breadcrumb items from a URL path
 * @param pathname - The current pathname (e.g., "/en/features/booking")
 * @param language - The current language
 * @returns Array of breadcrumb items
 */
export function generateBreadcrumbs(
  pathname: string,
  language: Language
): IBreadcrumbItem[] {
  // Remove leading/trailing slashes and split
  const segments = pathname.split('/').filter(Boolean);

  // If it's just the language, home page, or /en/home or /zh/home, return empty (no breadcrumbs)
  if (
    segments.length <= 1 ||
    pathname === `/${language}/home` ||
    pathname === `/${language}/home/`
  ) {
    return [];
  }

  const breadcrumbs: IBreadcrumbItem[] = [];

  // Add Home
  breadcrumbs.push({
    name: language === 'en' ? 'Home' : '首頁',
    href: `/${language}/home`,
  });

  // Path segment label mapping
  const labelMap: Record<string, { en: string; zh: string }> = {
    features: { en: 'Features', zh: '功能' },
    products: { en: 'Products', zh: '產品' },
    chatgpt: { en: 'ChatGPT Integration', zh: 'ChatGPT 整合' },
    scheduling: { en: 'Smart Scheduling System', zh: '智能排程系統' },
    appointments: { en: 'Online Appointment', zh: '線上預約網站' },
    management: { en: 'Business Management System', zh: '業務管理系統' },
    'branded-app': { en: 'Custom Branded App', zh: '客製化品牌APP' },
    'line-integration': { en: 'Line Booking Integration', zh: 'Line預約整合' },
    'online-shop': { en: 'Online Shop', zh: '線上商城' },
    'multi-language': { en: 'True Bilingual Platform', zh: '真正的雙語平台' },
    'data-security': { en: 'Enterprise Security Shield', zh: '企業安全護盾' },
    pricing: { en: 'Pricing', zh: '定價' },
    blog: { en: 'Blog', zh: '部落格' },
    changelog: { en: 'Changelog', zh: '更新紀錄' },
    'business-types': { en: 'Business Types', zh: '業務類型' },
    acupuncture: { en: 'Acupuncture', zh: '針灸' },
    beauty: { en: 'Beauty', zh: '美容' },
    hairdressers: { en: 'Hairdressers', zh: '美髮造型' },
    massage: { en: 'Massage', zh: '按摩' },
    'wellness-spa': { en: 'Wellness-Spa', zh: '健康水療' },
    badminton: { en: 'Badminton', zh: '羽毛球' },
    fitness: { en: 'Fitness', zh: '健身工作室' },
    'martial-arts': { en: 'Martial-Arts', zh: '武術格鬥' },
    'personal-trainer': { en: 'Personal-Trainer', zh: '私人教練' },
    pilates: { en: 'Pilates', zh: '皮拉提斯' },
    swimming: { en: 'Swimming', zh: '游泳' },
    tennis: { en: 'Tennis', zh: '網球' },
    'horse-riding': { en: 'Horse-Riding', zh: '馬術' },
    'yoga-dance': { en: 'Yoga-Dance', zh: '瑜珈舞蹈' },
    'ball-swimming': { en: 'Ball-Swimming', zh: '水療' },
    dentists: { en: 'Dentists', zh: '牙醫診所' },
    'education-training': { en: 'Education-Training', zh: '教育培訓' },
    'free-diving': { en: 'Free-Diving', zh: '自由潛水' },
    'success-stories': { en: 'Success Stories', zh: '成功案例' },
    'fangshi-badminton': { en: 'Fangshi Badminton', zh: '方式羽球' },
    pilaform: { en: 'PILAFORM', zh: 'PILAFORM' },
    'kc-studio': { en: 'KC STUDIO', zh: 'KC STUDIO' },
    'branded-apps': { en: 'Branded Apps', zh: '品牌應用程式' },
    about: { en: 'About', zh: '關於' },
    contact: { en: 'Contact', zh: '聯絡' },
    help: { en: 'Help Center', zh: '幫助中心' },
    faq: { en: 'FAQ', zh: '常見問題' },
    resources: { en: 'Resources', zh: '資源' },
    'who-we-are': { en: 'Who We Are', zh: '品牌故事' },
    booking: { en: 'All bookings', zh: '所有預約' },
    'intake-form': { en: 'Intake Form', zh: '顧客問卷' },
    reports: { en: 'reports', zh: '營運報告' },
    'terms-and-conditions': { en: 'Terms And Conditions', zh: '條款與合約' },
    'waiting-list': { en: 'Waiting List', zh: '候補名單' },
    'automated-emails': { en: 'Automated Emails', zh: '自動電子郵件' },
    'class-package': { en: 'Class Package', zh: '套裝課程' },
    'coupons-discounts': { en: 'Coupons & Discounts', zh: '優惠券 & 折扣' },
    'dynamic-instructor-pay': {
      en: 'Dynamic Instructor Pay',
      zh: '指導者薪資',
    },
    'google-calendar': { en: 'Google Calendar', zh: 'Google 行事曆' },
    'loyalty-system': { en: 'Loyalty System', zh: '消費回饋系統' },
    'multiple-locations': { en: 'Multiple Locations', zh: '支援多間分店管理' },
    'online-payment': { en: 'Online Payment', zh: '線上付款' },
    'online-teaching': { en: 'Online Teaching', zh: '線上教學' },
    'pass-terms-conditions': {
      en: 'Pass Terms Conditions',
      zh: '課卡條款與合約',
    },
    'pause-accounts': { en: 'Pause Accounts', zh: '暫延帳戶' },
    'photo-album': { en: 'Photo Album', zh: '相簿' },
    'point-system': { en: 'Point System', zh: '點數系統' },
    'sales-commission': { en: 'Sales Commission', zh: '銷售分潤' },
    'advanced-notifications': {
      en: 'Advanced Notifications',
      zh: 'APP 進階通知',
    },
    facebook: { en: 'Facebook News', zh: 'Facebook 動態消息' },
    'google-analytics': { en: 'Google Analytics', zh: 'Google 分析' },
    'google-business': { en: 'Google Business', zh: 'Google 商家' },
    instagram: { en: 'Instagram Gallery', zh: 'Instagram 動態牆' },
    'instant-messaging': { en: 'Instant Messaging', zh: '即時通訊' },
    'line-bot': { en: 'Line Bot', zh: 'Line 機器人' },
    'whatsapp-bot': { en: 'WhatsApp AI Bot', zh: 'WhatsApp AI 機器人' },
    'wechat-bot': { en: 'WeChat AI Bot', zh: '微信 AI 機器人' },
    'about-us': { en: 'About Us', zh: '關於我們' },
    api: { en: 'API', zh: 'API' },
    crm: { en: 'Customer Relationship Management', zh: '客戶關係管理' },
    'custom-domain': { en: 'Custom Domain', zh: '客製化網域' },
    'custom-page': { en: 'Custom Page', zh: '客製化頁面' },
    gate: { en: 'Gate', zh: '閘門系統' },
    'kiosk-instructor-checkin': {
      en: 'Kiosk Instructor Checkin',
      zh: '指導者簽到系統',
    },
    'kiosk-mode': { en: 'Kiosk Mode', zh: '客戶簽到系統' },
    'news-blog-announcements': {
      en: 'News & Blog Announcements',
      zh: '最新消息 & 部落格',
    },
    'receipt-printer': { en: 'Receipt Printer', zh: '列印發票收據' },
    shop: { en: 'Shop', zh: '商城' },
    'website-integration': { en: 'Website Integration', zh: '網站整合' },
  };

  // Build breadcrumbs from path segments
  let currentPath = `/${language}`;

  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    // Skip structural segments that have no page of their own
    // (e.g. /help-center/category/<slug>)
    if (segment === 'category' && segments[i - 1] === 'help-center') {
      continue;
    }

    // Get label from map or capitalize segment
    const label =
      labelMap[segment]?.[language] ||
      segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    // Last segment is current page (no link)
    if (i === segments.length - 1) {
      breadcrumbs.push({
        name: label,
      });
    } else {
      // Intermediate segments have links
      breadcrumbs.push({
        name: label,
        href: `${currentPath}/`,
      });
    }
  }

  return breadcrumbs;
}
